import Konva from "konva";
import { Point } from "shared/datamodel/schemas";

const COLLISION_PADDING = 30;

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Checks if two rectangles intersect (with padding)
 */
export function rectsIntersect(a: Rect, b: Rect): boolean {
  return !(
    a.x + a.width + COLLISION_PADDING <= b.x ||
    b.x + b.width + COLLISION_PADDING <= a.x ||
    a.y + a.height + COLLISION_PADDING <= b.y ||
    b.y + b.height + COLLISION_PADDING <= a.y
  );
}

/**
 * Checks if a candidate rectangle collides with any existing element
 */
export function hasAnyCollision(candidateRect: Rect, existingRects: Rect[]): boolean {
  return existingRects.some((rect) => rectsIntersect(candidateRect, rect));
}

const DEFAULT_POSITION: Point = { x: 0, y: 0 };

/**
 * Finds a non-colliding position for placing new elements on the canvas.
 *
 * Strategy: Find ANY position that doesn't collide with existing elements.
 * No bounding box concept - just collision detection with actual elements.
 *
 * @param elementsBbox - The bounding box of the elements to be placed (template size)
 * @param layerRef - Reference to the Konva layer containing canvas elements
 * @param initialPosition - Fallback position if no elements exist
 * @returns A point (center) where the elements can be placed without collision
 */
export function findNonCollidingPosition(
  elementsBbox: Rect,
  layerRef: React.RefObject<Konva.Layer>,
  initialPosition: Point = DEFAULT_POSITION
): Point {
  const existingRects = getExistingElementRects(layerRef);
  return findNonCollidingPositionWithRects(elementsBbox, existingRects, initialPosition);
}

export function findNonCollidingPositionWithRects(
  elementsBbox: Rect,
  existingRects: Rect[],
  initialPosition: Point = DEFAULT_POSITION
): Point {
  if (existingRects.length === 0) {
    return initialPosition;
  }

  const templateWidth = elementsBbox.width;
  const templateHeight = elementsBbox.height;

  // First, check if initialPosition (viewport center) is non-colliding
  const initialTopLeftX = initialPosition.x - templateWidth / 2;
  const initialTopLeftY = initialPosition.y - templateHeight / 2;
  const initialCandidateRect: Rect = {
    x: initialTopLeftX,
    y: initialTopLeftY,
    width: templateWidth,
    height: templateHeight,
  };

  // If viewport center is non-colliding, use it
  if (!hasAnyCollision(initialCandidateRect, existingRects)) {
    return initialPosition;
  }

  // Otherwise, find a non-colliding position based purely on element edges
  // Prioritize positions near the viewport center (initialPosition)
  const position = findNonCollidingSpot(templateWidth, templateHeight, existingRects, initialPosition);

  return {
    x: position.x + templateWidth / 2,
    y: position.y + templateHeight / 2,
  };
}

export function getExistingElementRects(layerRef: React.RefObject<Konva.Layer>): Rect[] {
  if (!layerRef?.current) {
    return [];
  }

  const allNodes = layerRef.current
    .find((node: unknown) => {
      const konvaNode = node as Konva.Node;
      return konvaNode.attrs?.isCanvasElement && konvaNode.isVisible();
    })
    .toArray();

  return allNodes.map((node: Konva.Node) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const stage = node.getStage() as any;
    return node.getClientRect({
      relativeTo: stage,
      skipShadow: true,
    });
  });
}

/**
 * Calculates distance from a candidate position (top-left corner) to a reference point
 */
function distanceToPoint(x: number, y: number, width: number, height: number, point: Point): number {
  const candidateCenterX = x + width / 2;
  const candidateCenterY = y + height / 2;
  return Math.sqrt(Math.pow(candidateCenterX - point.x, 2) + Math.pow(candidateCenterY - point.y, 2));
}

/**
 * Finds a position where the template doesn't collide with any element.
 *
 * Prioritizes positions CLOSE to the preferred position (typically viewport center).
 * This keeps templates near where the user is looking rather than far away.
 */
function findNonCollidingSpot(
  templateWidth: number,
  templateHeight: number,
  existingRects: Rect[],
  preferredPosition: Point
): Point {
  // Generate candidate positions from element edges
  const candidates: Point[] = [];

  for (const rect of existingRects) {
    // Positions adjacent to each element
    candidates.push(
      { x: rect.x - templateWidth - COLLISION_PADDING, y: rect.y }, // left of element
      { x: rect.x + rect.width + COLLISION_PADDING, y: rect.y }, // right of element
      { x: rect.x, y: rect.y - templateHeight - COLLISION_PADDING }, // above element
      { x: rect.x, y: rect.y + rect.height + COLLISION_PADDING } // below element
    );
  }

  const preferredTopLeftX = preferredPosition.x - templateWidth / 2;
  const preferredTopLeftY = preferredPosition.y - templateHeight / 2;

  // Add positions around the preferred position in a spiral pattern
  const spiralOffsets = [
    { x: 0, y: 0 }, // exact position
    { x: templateWidth + COLLISION_PADDING, y: 0 }, // right
    { x: -(templateWidth + COLLISION_PADDING), y: 0 }, // left
    { x: 0, y: templateHeight + COLLISION_PADDING }, // below
    { x: 0, y: -(templateHeight + COLLISION_PADDING) }, // above
    { x: templateWidth + COLLISION_PADDING, y: templateHeight + COLLISION_PADDING }, // bottom-right
    { x: -(templateWidth + COLLISION_PADDING), y: templateHeight + COLLISION_PADDING }, // bottom-left
    { x: templateWidth + COLLISION_PADDING, y: -(templateHeight + COLLISION_PADDING) }, // top-right
    { x: -(templateWidth + COLLISION_PADDING), y: -(templateHeight + COLLISION_PADDING) }, // top-left
  ];

  for (const offset of spiralOffsets) {
    candidates.push({
      x: preferredTopLeftX + offset.x,
      y: preferredTopLeftY + offset.y,
    });
  }

  // Sort candidates by distance to preferred position (viewport center) - closest first
  candidates.sort((a, b) => {
    const distanceA = distanceToPoint(a.x, a.y, templateWidth, templateHeight, preferredPosition);
    const distanceB = distanceToPoint(b.x, b.y, templateWidth, templateHeight, preferredPosition);
    return distanceA - distanceB;
  });

  // Find first non-colliding position (closest to preferred position wins)
  for (const candidate of candidates) {
    const candidateRect: Rect = { x: candidate.x, y: candidate.y, width: templateWidth, height: templateHeight };

    if (!hasAnyCollision(candidateRect, existingRects)) {
      return candidate;
    }
  }

  // Fallback: place near preferred position if possible
  const fallbackRect: Rect = {
    x: preferredTopLeftX,
    y: preferredTopLeftY,
    width: templateWidth,
    height: templateHeight,
  };

  if (!hasAnyCollision(fallbackRect, existingRects)) {
    return { x: preferredTopLeftX, y: preferredTopLeftY };
  }

  // Last resort: place after the rightmost element
  let maxRight = 0;
  let topY = 0;
  for (const rect of existingRects) {
    if (rect.x + rect.width > maxRight) {
      maxRight = rect.x + rect.width;
      topY = rect.y;
    }
  }

  return { x: maxRight + COLLISION_PADDING, y: topY };
}
